<template>
  <div class="container-xl px-4 import-products-page">
    <div class="row">
      <div class="col-12">
        <div class="page-header d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4 mt-2">
          <div>
            <!-- La importación con IA es un paso dentro del alta de productos:
                 se llega desde el botón partido de Productos, así que tiene que
                 haber camino de vuelta. No se navega solo al terminar: el flujo
                 permite importar varios seguidos. -->
            <router-link :to="{ name: 'InvProductos' }" class="page-back-link">
              <i class="bi bi-arrow-left me-1"></i> Volver a Productos
            </router-link>
            <p class="page-eyebrow mb-1">Agente de productos</p>
            <h2 class="fw-bold mb-1">Importación asistida de productos</h2>
            <p class="page-copy mb-0">Puedes importar usando código de barras, código interno o pistas manuales como nombre, marca y presentación.</p>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <div class="col-12 col-xl-8">
        <div class="card shadow-sm border-0 import-card">
          <div class="card-body p-4">
            <form class="import-form" @submit.prevent="submitImport">
              <div class="row g-3">
                <div class="col-12 col-md-6">
                  <label for="barcode" class="form-label fw-semibold">Código de barras</label>
                  <input
                    id="barcode"
                    ref="barcodeInput"
                    v-model.trim="importForm.barcode"
                    type="text"
                    class="form-control form-control-lg"
                    inputmode="numeric"
                    autocomplete="off"
                    placeholder="7794640173066"
                    :disabled="loading"
                  >
                </div>
                <div class="col-12 col-md-6">
                  <label for="codigo-interno" class="form-label fw-semibold">Código interno</label>
                  <input
                    id="codigo-interno"
                    v-model.trim="importForm.codigo_interno"
                    type="text"
                    class="form-control form-control-lg"
                    autocomplete="off"
                    placeholder="PROD-0001"
                    :disabled="loading"
                  >
                </div>
                <div class="col-12">
                  <label for="nombre" class="form-label fw-semibold">Nombre o tipo de producto</label>
                  <input
                    id="nombre"
                    v-model.trim="importForm.nombre"
                    type="text"
                    class="form-control"
                    autocomplete="off"
                    placeholder="Yerba mate compuesta"
                    :disabled="loading"
                  >
                </div>
                <div class="col-12 col-md-6">
                  <label for="marca" class="form-label fw-semibold">Marca</label>
                  <input
                    id="marca"
                    v-model.trim="importForm.marca"
                    type="text"
                    class="form-control"
                    autocomplete="off"
                    placeholder="Kurupi"
                    :disabled="loading"
                  >
                </div>
                <div class="col-12 col-md-6">
                  <label for="presentacion" class="form-label fw-semibold">Presentación, contenido o distintivo</label>
                  <input
                    id="presentacion"
                    v-model.trim="importForm.presentacion"
                    type="text"
                    class="form-control"
                    autocomplete="off"
                    placeholder="500g / lata / aloe vera"
                    :disabled="loading"
                  >
                </div>
              </div>
              <div class="import-actions mt-3">
                <small class="text-muted">
                  Código de barras y código interno son opcionales. Si no existen, completa al menos nombre, marca o presentación.
                </small>
                <button type="submit" class="btn btn-dark px-4" :disabled="loading || !canSubmitImport">
                  {{ loading ? 'Procesando...' : 'Importar' }}
                </button>
              </div>
            </form>

            <div v-if="feedback.message" class="mt-3 alert mb-0" :class="feedbackClass" role="status">
              {{ feedback.message }}
            </div>

            <div v-if="lastResult" class="result-summary mt-4">
              <div class="result-summary__header">
                <span class="result-badge" :class="`result-badge--${lastResult.status}`">{{ statusLabel(lastResult.status) }}</span>
                <small v-if="lastResult.request_id" class="text-muted">Solicitud {{ lastResult.request_id }}</small>
              </div>

              <div v-if="lastResult.product_draft" class="draft-grid mt-3">
                <div class="draft-item">
                  <span class="draft-label">Nombre sugerido</span>
                  <strong>{{ lastResult.product_draft.nombre }}</strong>
                </div>
                <div class="draft-item">
                  <span class="draft-label">Unidad</span>
                  <strong>{{ unitLabel(lastResult.product_draft.unidad_medida_id) }}</strong>
                </div>
                <div class="draft-item">
                  <span class="draft-label">IVA</span>
                  <strong>{{ ivaLabel(lastResult.product_draft.tipo_iva) }}</strong>
                </div>
                <div class="draft-item draft-item--full">
                  <span class="draft-label">Descripción sugerida</span>
                  <strong>{{ lastResult.product_draft.descripcion }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-4">
        <div class="card shadow-sm border-0 helper-card h-100">
          <div class="card-body p-4">
            <h3 class="helper-title">Flujo operativo</h3>
            <ol class="helper-list mb-0">
              <li>Leer un barcode, cargar un código interno o usar pistas manuales.</li>
              <li>Si hace falta, agregar nombre, marca y presentación como pistas manuales.</li>
              <li>El API principal envía el payload completo al agente.</li>
              <li>Si falta categoría, eliges una existente o creas una nueva.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <AppModal
      modal-id="agentCategoryConfirmationModal"
      :show-modal="showCategoryModal"
      title="Confirmar categoría"
      dialog-class="modal-lg modal-dialog-centered"
      content-class="agent-category-modal"
      with-backdrop
      @close="closeCategoryModal"
    >
      <template #body>
        <div v-if="pendingCategoryConfirmation" class="category-confirmation">
          <div class="confirmation-block">
            <p class="confirmation-label">Producto sugerido</p>
            <h4 class="confirmation-title">{{ pendingCategoryConfirmation.product_draft?.nombre || 'Producto' }}</h4>
            <p class="confirmation-copy mb-0">{{ pendingCategoryConfirmation.product_draft?.descripcion || 'Sin descripción sugerida.' }}</p>
          </div>

          <div class="confirmation-grid">
            <div class="confirmation-chip">
              <span>Unidad</span>
              <strong>{{ unitLabel(pendingCategoryConfirmation.product_draft?.unidad_medida_id) }}</strong>
            </div>
            <div class="confirmation-chip">
              <span>IVA</span>
              <strong>{{ ivaLabel(pendingCategoryConfirmation.product_draft?.tipo_iva) }}</strong>
            </div>
          </div>

          <div class="proposal-box">
            <p class="confirmation-label">Propuesta del agente</p>
            <strong>{{ pendingCategoryConfirmation.proposal?.nombre_categoria_sugerida }}</strong>
            <p class="mb-2">{{ pendingCategoryConfirmation.proposal?.descripcion }}</p>
            <small class="text-muted">{{ pendingCategoryConfirmation.proposal?.justificacion }}</small>
          </div>

          <div class="mode-switch">
            <button
              type="button"
              class="btn"
              :class="!createCategory ? 'btn-dark' : 'btn-outline-dark'"
              @click="createCategory = false"
            >
              Usar categoría existente
            </button>
            <button
              type="button"
              class="btn"
              :class="createCategory ? 'btn-dark' : 'btn-outline-dark'"
              @click="createCategory = true"
            >
              Crear categoría nueva
            </button>
          </div>

          <div v-if="!createCategory" class="mt-3">
            <label class="form-label fw-semibold">Categoría existente</label>
            <CategorySelect v-model="selectedCategoryId" :disabled="confirmingCategory" />
          </div>

          <div v-else class="row g-3 mt-1">
            <div class="col-12">
              <label class="form-label fw-semibold" for="new-category-name">Nombre</label>
              <input id="new-category-name" v-model.trim="newCategory.nombre" type="text" class="form-control" :disabled="confirmingCategory">
            </div>
            <div class="col-12">
              <label class="form-label fw-semibold" for="new-category-description">Descripción</label>
              <textarea id="new-category-description" v-model.trim="newCategory.descripcion" class="form-control" rows="3" :disabled="confirmingCategory"></textarea>
            </div>
          </div>

          <div v-if="categoryModalError" class="alert alert-danger mt-3 mb-0">
            {{ categoryModalError }}
          </div>
        </div>
      </template>
      <template #footer>
        <button type="button" class="btn btn-outline-secondary" :disabled="confirmingCategory" @click="closeCategoryModal">
          Cancelar
        </button>
        <button type="button" class="btn btn-dark" :disabled="confirmingCategory" @click="confirmCategorySelection">
          {{ confirmingCategory ? 'Confirmando...' : 'Confirmar importación' }}
        </button>
      </template>
    </AppModal>

    <RegistrarProductoModal
      :show-modal="showResultModal"
      title="Producto importado"
      :product="resultProduct"
      :read-only="false"
      :allow-delete="canRevertImportedProduct"
      :deleting="revertingProduct"
      @update:showModal="handleResultModalVisibilityChange"
      @delete-product="revertImportedProduct"
      @product-registered="handleResultSaved"
    />
  </div>
</template>

<script>
import AppModal from '@/components/AppModal.vue';
import CategorySelect from '@/components/CategorySelect.vue';
import RegistrarProductoModal from '@/components/RegistrarProductoModal.vue';
import apiService from '@/services/apiService';
import productImportAgentService from '@/services/productImportAgentService';

const DEFAULT_FEEDBACK = {
  type: 'secondary',
  message: ''
};

export default {
  name: 'ImportacionProductosView',
  components: {
    AppModal,
    CategorySelect,
    RegistrarProductoModal
  },
  data() {
    return {
      importForm: {
        barcode: '',
        codigo_interno: '',
        nombre: '',
        marca: '',
        presentacion: ''
      },
      loading: false,
      feedback: { ...DEFAULT_FEEDBACK },
      lastResult: null,
      showCategoryModal: false,
      pendingCategoryConfirmation: null,
      selectedCategoryId: null,
      createCategory: false,
      newCategory: {
        nombre: '',
        descripcion: ''
      },
      categoryModalError: '',
      confirmingCategory: false,
      showResultModal: false,
      resultProduct: null,
      revertingProduct: false
    };
  },
  computed: {
    canSubmitImport() {
      return ['barcode', 'codigo_interno', 'nombre', 'marca', 'presentacion']
        .some((key) => Boolean(String(this.importForm[key] || '').trim()));
    },
    canRevertImportedProduct() {
      return this.lastResult?.status === 'created' && Boolean(this.resultProduct?.id);
    },
    feedbackClass() {
      return {
        'alert-success': this.feedback.type === 'success',
        'alert-warning': this.feedback.type === 'warning',
        'alert-danger': this.feedback.type === 'danger',
        'alert-secondary': !['success', 'warning', 'danger'].includes(this.feedback.type)
      };
    }
  },
  mounted() {
    this.focusBarcodeInput();
  },
  methods: {
    // Dos causas hacían que el foco no quedara acá pese a pedirlo (§5.1 del
    // SPEC): el input está `:disabled="loading"` y `focus()` sobre un input
    // deshabilitado es un no-op silencioso; y los modales se montan después de
    // la respuesta y se llevan el foco. Por eso: se espera a que `loading` sea
    // false, se pide el foco al cerrar el modal —no al responder— y se
    // reintenta un frame si el foco no aterrizó.
    focusBarcodeInput() {
      const aplicar = () => {
        const input = this.$refs.barcodeInput;
        if (!input || input.disabled) return false;
        input.focus();
        input.select?.();
        return document.activeElement === input;
      };
      this.$nextTick(() => {
        if (aplicar()) return;
        // Un solo frame de gracia: alcanza para que termine la transición de
        // cierre del modal. Un timeout largo taparía el problema y produciría
        // saltos de foco visibles.
        requestAnimationFrame(() => { aplicar(); });
      });
    },
    resetFeedback() {
      this.feedback = { ...DEFAULT_FEEDBACK };
    },
    setFeedback(type, message) {
      this.feedback = { type, message };
    },
    statusLabel(status) {
      const labels = {
        created: 'Creado',
        duplicate_barcode: 'Duplicado',
        category_confirmation_required: 'Requiere categoría',
        insufficient_information: 'Información insuficiente'
      };
      return labels[status] || status;
    },
    unitLabel(value) {
      return Number(value) === 2 ? 'Gramos' : Number(value) === 7 ? 'Unidad' : 'Sin definir';
    },
    ivaLabel(value) {
      return Number(value) === 1 ? 'IVA 10%' : Number(value) === 2 ? 'IVA 5%' : Number(value) === 3 ? 'Exento' : 'Sin definir';
    },
    buildErrorMessage(error, fallback = 'No se pudo completar la operación.') {
      return (
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        fallback
      );
    },
    normalizeProductForModal(product) {
      if (!product) return null;
      return {
        id: product.producto_id ?? product.id ?? null,
        code: product.codigo_barras || product.codigo_producto || product.codigo || '',
        codigo_barras: product.codigo_barras || '',
        codigo_producto: product.codigo_producto || '',
        codigo_plu_balanza: product.codigo_plu_balanza || '',
        es_granel: Boolean(product.es_granel),
        name: product.nombre || product.name || '',
        descripcion: product.descripcion || '',
        url_imagen: product.url_imagen || '',
        categoria_id: product.categoria_id ?? null,
        unidad_medida_id: product.unidad_medida_id ?? null,
        activo: product.activo !== false,
        tipo_iva: product.tipo_iva ?? 1
      };
    },
    buildImportPayload() {
      const payload = {};
      ['barcode', 'codigo_interno', 'nombre', 'marca', 'presentacion'].forEach((key) => {
        const value = String(this.importForm[key] || '').trim();
        if (value) {
          payload[key] = value;
        }
      });
      return payload;
    },
    resetImportForm() {
      this.importForm = {
        barcode: '',
        codigo_interno: '',
        nombre: '',
        marca: '',
        presentacion: ''
      };
    },
    async openCreatedProduct(productSummary) {
      try {
        const { data } = await apiService.get(`/api/products/${productSummary.producto_id}`);
        this.resultProduct = this.normalizeProductForModal(data);
      } catch (error) {
        this.resultProduct = this.normalizeProductForModal(productSummary);
      }
      this.showResultModal = true;
    },
    async handleImportResponse(payload) {
      this.lastResult = payload;
      const status = payload?.status;

      if (status === 'created') {
        this.setFeedback('success', 'Producto creado correctamente. Puedes revisarlo y editarlo en el modal.');
        await this.openCreatedProduct(payload.product);
        this.resetImportForm();
        this.closeCategoryModal();
        // El foco NO se pide acá: el modal de resultado se monta a
        // continuación y se lo llevaría. Se pide al cerrarlo
        // (`handleResultModalVisibilityChange` / `handleResultSaved`).
        return;
      }

      if (status === 'duplicate_barcode') {
        this.setFeedback('warning', payload?.message || 'El código de barras ya existe en el sistema.');
        this.closeCategoryModal();
        this.focusBarcodeInput();
        return;
      }

      if (status === 'category_confirmation_required') {
        this.pendingCategoryConfirmation = payload;
        this.selectedCategoryId = null;
        this.createCategory = false;
        this.newCategory = {
          nombre: payload?.proposal?.nombre_categoria_sugerida || '',
          descripcion: payload?.proposal?.descripcion || ''
        };
        this.categoryModalError = '';
        this.showCategoryModal = true;
        this.setFeedback('warning', 'El agente necesita confirmación de categoría antes de crear el producto.');
        return;
      }

      if (status === 'insufficient_information') {
        this.setFeedback('danger', payload?.message || 'No hay evidencia suficiente para crear el producto.');
        this.closeCategoryModal();
        this.focusBarcodeInput();
        return;
      }

      this.setFeedback('danger', payload?.message || 'Respuesta no reconocida del agente.');
    },
    async submitImport() {
      const requestPayload = this.buildImportPayload();
      if (!Object.keys(requestPayload).length) {
        this.setFeedback('danger', 'Ingresa un código de barras, código interno o alguna pista manual.');
        this.focusBarcodeInput();
        return;
      }

      this.loading = true;
      this.resetFeedback();
      this.lastResult = null;

      try {
        const { data } = await productImportAgentService.importProduct(requestPayload);
        await this.handleImportResponse(data);
      } catch (error) {
        const payload = error?.response?.data;
        if (payload?.status) {
          await this.handleImportResponse(payload);
        } else {
          this.setFeedback('danger', this.buildErrorMessage(error, 'No se pudo importar el producto.'));
        }
      } finally {
        this.loading = false;
        // Recién acá el input deja de estar deshabilitado y `focus()` puede
        // aterrizar. Si el modal de resultado quedó abierto, éste no roba el
        // foco: `focusBarcodeInput` verifica `document.activeElement` y el
        // modal lo recupera por su cuenta.
        if (!this.showResultModal && !this.showCategoryModal) {
          this.focusBarcodeInput();
        }
      }
    },
    closeCategoryModal() {
      const estabaAbierto = this.showCategoryModal;
      this.showCategoryModal = false;
      this.confirmingCategory = false;
      this.categoryModalError = '';
      // Cancelar la confirmación de categoría también devuelve el cursor al
      // lector: es uno de los cinco desenlaces de §5.2 del SPEC.
      if (estabaAbierto && !this.showResultModal && !this.loading) {
        this.focusBarcodeInput();
      }
    },
    async confirmCategorySelection() {
      if (!this.pendingCategoryConfirmation?.request_id) {
        this.categoryModalError = 'No hay una solicitud pendiente para confirmar.';
        return;
      }

      let payload = { request_id: this.pendingCategoryConfirmation.request_id };
      const identifierOverrides = {};
      if (this.importForm.barcode?.trim()) {
        identifierOverrides.barcode = this.importForm.barcode.trim();
      }
      if (this.importForm.codigo_interno?.trim()) {
        identifierOverrides.codigo_interno = this.importForm.codigo_interno.trim();
      }

      if (this.createCategory) {
        if (!this.newCategory.nombre || !this.newCategory.descripcion) {
          this.categoryModalError = 'Completa nombre y descripción de la nueva categoría.';
          return;
        }
        payload = {
          ...payload,
          ...identifierOverrides,
          create_category: true,
          categoria: {
            nombre: this.newCategory.nombre,
            descripcion: this.newCategory.descripcion
          }
        };
      } else {
        if (!this.selectedCategoryId) {
          this.categoryModalError = 'Selecciona una categoría existente.';
          return;
        }
        payload = {
          ...payload,
          ...identifierOverrides,
          categoria_id: Number(this.selectedCategoryId)
        };
      }

      this.confirmingCategory = true;
      this.categoryModalError = '';

      try {
        const { data } = await productImportAgentService.confirmCategory(payload);
        await this.handleImportResponse(data);
      } catch (error) {
        const responsePayload = error?.response?.data;
        if (responsePayload?.status) {
          await this.handleImportResponse(responsePayload);
        } else {
          this.categoryModalError = this.buildErrorMessage(error, 'No se pudo confirmar la categoría.');
        }
      } finally {
        this.confirmingCategory = false;
        if (!this.showResultModal && !this.showCategoryModal) {
          this.focusBarcodeInput();
        }
      }
    },
    async revertImportedProduct() {
      const productId = this.resultProduct?.id;
      if (!productId) {
        this.setFeedback('danger', 'No se pudo identificar el producto a revertir.');
        return;
      }

      // `await`: `window.confirm` es el diálogo de la app y devuelve una PROMESA;
      // sin esperarla, «Cancelar» no cancelaba (FIX_HALLAZGOS_DE_VERIFICACION, F-2).
      if (!(await window.confirm('Se eliminará el producto recién creado. ¿Deseas continuar?'))) {
        return;
      }

      this.revertingProduct = true;
      try {
        await apiService.delete(`/api/products/${productId}`);
        this.showResultModal = false;
        this.resultProduct = null;
        this.lastResult = null;
        this.setFeedback('success', 'La carga fue revertida y el producto se eliminó correctamente.');
        this.focusBarcodeInput();
      } catch (error) {
        this.setFeedback('danger', this.buildErrorMessage(error, 'No se pudo revertir la carga del producto.'));
      } finally {
        this.revertingProduct = false;
      }
    },
    handleResultModalVisibilityChange(value) {
      this.showResultModal = value;
      if (!value) {
        this.focusBarcodeInput();
      }
    },
    handleResultSaved() {
      this.setFeedback('success', 'Producto actualizado correctamente.');
      this.showResultModal = false;
      this.focusBarcodeInput();
    }
  }
};
</script>

<style scoped>
.import-products-page {
  padding-bottom: 2rem;
}

.page-header {
  min-height: 84px;
}

.page-back-link {
  display: inline-flex;
  align-items: center;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  text-decoration: none;
}

.page-back-link:hover,
.page-back-link:focus {
  color: #0f172a;
  text-decoration: underline;
}

.page-eyebrow {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
}

.page-copy {
  max-width: 720px;
  color: #475569;
}

.import-card,
.helper-card {
  border-radius: 20px;
}

.import-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.85rem;
}

.result-summary {
  border-top: 1px solid #e2e8f0;
  padding-top: 1rem;
}

.result-summary__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

.result-badge {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.result-badge--created {
  background: #dcfce7;
  color: #166534;
}

.result-badge--duplicate_barcode,
.result-badge--category_confirmation_required {
  background: #fef3c7;
  color: #92400e;
}

.result-badge--insufficient_information {
  background: #fee2e2;
  color: #b91c1c;
}

.draft-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.draft-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  background: #f8fafc;
}

.draft-item--full {
  grid-column: 1 / -1;
}

.draft-label,
.confirmation-label {
  font-size: 0.74rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.helper-title {
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-weight: 700;
}

.helper-list {
  padding-left: 1.1rem;
  color: #334155;
}

.helper-list li + li {
  margin-top: 0.65rem;
}

.category-confirmation {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.confirmation-block,
.proposal-box {
  padding: 1rem;
  border-radius: 16px;
  background: #f8fafc;
}

.confirmation-title {
  margin-bottom: 0.35rem;
  font-size: 1.2rem;
}

.confirmation-copy {
  color: #475569;
}

.confirmation-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
}

.confirmation-chip {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
}

.mode-switch {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

@media (max-width: 767px) {
  .import-actions {
    align-items: stretch;
  }

  .import-actions .btn {
    width: 100%;
  }

  .draft-grid,
  .confirmation-grid {
    grid-template-columns: 1fr;
  }

  .page-header {
    min-height: auto;
  }
}
</style>
